import { useEffect,useState } from 'react';

import { useAppDispatch } from 'app/hooks';
import { fetchAclWithProfileEntries } from 'common/services/api/Acls/AclEntries.thunk';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { AclsWithProfiles, AppSuiteTenant, License } from 'common/types/Entities';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { _formatProductEntitlementsWithLicenseInfo } from '../helpers';
import { ReportProductEntitlements } from '../typeDefs';


export const useFetchLicensesProductEntitlements = (licenses: License[], currentTenant: AppSuiteTenant): {
    processing: boolean,
    productEntitlements: ReportProductEntitlements[]
} => {
    const [productEntitlements, setProductEntitlements] = useState<ReportProductEntitlements[]>([]);
    const [processing, setProcessing] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { id: tenantId, isLicenseRequired } = currentTenant;

    const _handleError = (error: unknown) => {
        const err = error as Error;
        const statusCode = extractStatusCode(err.message);
        handleListFetchError(statusCode, 'Product Entitlements', enqueueSnackbar);
        setProcessing(false);
    };

    const _getFetchAclEntriesPromises = () => {
        const fetchAclEntriesPromises = [];

        for (const license of licenses) {
            fetchAclEntriesPromises.push(
                dispatch(
                    fetchAclWithProfileEntries({
                        tenantId: tenantId,
                        licenseId: license.id
                    })
                )
                    .unwrap()
                    .then((response) => {
                        return response.data;
                    })
                    .catch((error) => { _handleError(error); })
            );
        }

        return fetchAclEntriesPromises;
    };

    const _getLicenseEntries = (licenses: License[]) => {
        return licenses.filter(item => !!item);
    };

    useEffect(() => {
        setProcessing(true);

        const fetchAclEntriesPromises = _getFetchAclEntriesPromises();
        Promise
            .all(fetchAclEntriesPromises)
            .then((aclEntriesByLicenseId) => {
                const aclEntries = aclEntriesByLicenseId
                    .filter((item): item is AclsWithProfiles[] => !!item && item.length > 0)
                    .flat();

                const licenseEntries = _getLicenseEntries(licenses);
                const formattedProductEntitlements = _formatProductEntitlementsWithLicenseInfo({
                    acls: aclEntries,
                    licenses: licenseEntries,
                    isLicenseRequired
                });

                setProductEntitlements(formattedProductEntitlements);
                setProcessing(false);
            }).catch((error) => { _handleError(error); });
    }, []);

    return {
        processing,
        productEntitlements
    };
};
